import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/path';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { OrderValue, SortOrder } from 'types/common';
import { deleteAppointment, getAppointments } from 'services/appointmentService';
import { useDidMount } from 'hooks/useDidMount';
import {
    APPOINTMENT_STATUS_OPTIONS,
    AppointmentTableParams,
    State,
    initialAppointmentsTableParams,
    initialState,
    tableColumns,
} from './constants';
import { BackdropLoader } from 'components/@shared/Loaders';
import TextField from '@mui/material/TextField';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { mainHeaderHeight } from 'theme/constants';
import { NoListYet } from 'components/@shared/NoListYet';
import { NoResultsFound } from 'components/@shared/NoResultsFound';
import { AppointmentResponseViewModel } from 'types/appointment';
import { AppointmentsActionsMenu } from 'components/Appointments/ItemActionsMenu';
import { MobileTable } from 'components/@shared/TableRemotePagination/MobileTable/MobileTable';
import { AppointmentListCell } from 'components/Appointments/List/Row/AppointmentListCell';
import { ITableHeadCell } from 'components/@shared/TableRemotePagination/types';
import { AdaptiveTable } from 'components/@shared/TableRemotePagination/AdaptiveTable';
import { useModal } from 'hooks/useModal';
import { WarningPopUp } from 'components/@shared/WarningPopUp';
import { parametrizeURL } from 'routes/utils';
import { AppointmentListRow } from 'components/Appointments/List/Row';
import { TableFilters } from 'components/@shared/TableFilters';
import { TableFiltersState } from 'types/common';
import { getFilterParams } from './utils';
import { getHasFilterParams } from 'utils/getHasFilterParams';
import { useDeniedAccess } from 'hooks/useDeniedAccess';
import { ONLY_CONTRACTOR, ONLY_USER } from 'constants/roles';
import { useRolePermissions } from 'hooks/useRolePermissions';
import { Headline } from 'components/@shared/Headline';

export const AppointmentsPage = () => {
    const [state, setState] = useState<State>(initialState);
    const [selectedItem, setSelectedItem] = useState({ name: '', id: '' });
    const navigate = useNavigate();

    const removeModal = useModal();

    const loadList = useCallback(
        (param: Partial<AppointmentTableParams>) => {
            const updatedParams: AppointmentTableParams = { ...state.tableParams, ...param };
            const { sortOrder, ...requestParams } = updatedParams;
            getAppointments({ ...requestParams, sortingOrder: Number(sortOrder === 'asc') as SortOrder })
                .then(({ items, count }) => {
                    setState((prevState) => ({
                        ...prevState,
                        isLoading: false,
                        list: items,
                        tableParams: updatedParams,
                        totalCount: count,
                    }));
                })
                .catch(() => {
                    setState((prevState) => ({ ...prevState, isLoading: false }));
                });
        },
        [state.tableParams],
    );

    const sortHandler = useCallback(
        (sortOrder: OrderValue, orderBy: string) => {
            loadList({ sortOrder, orderBy });
        },
        [loadList],
    );

    const setPageHandler = useCallback(
        (page: number) => {
            loadList({ page });
        },
        [loadList],
    );

    const setRowsPerPageHandler = useCallback(
        (pageSize: number) => {
            loadList({ page: 0, pageSize });
        },
        [loadList],
    );

    const searchChangeHandler = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        loadList({ page: 0, search: event.target.value });
    }, 500);

    const createNewAppointment = useCallback(() => navigate(PATH.CREATE_APPOINTMENT), [navigate]);

    const confirmDeleteHandler = useCallback(() => {
        removeModal.closeModal();
        setState((prevState) => ({ ...prevState, isLoading: true }));
        deleteAppointment(selectedItem.id)
            .then(() => loadList(initialAppointmentsTableParams))
            .catch(() => {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            });
    }, [state, removeModal, selectedItem]);

    const editHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.EDIT_APPOINTMENT, { id }));
        },
        [navigate],
    );

    const addProposalHandler = useCallback(
        (appointmentId: string) => {
            navigate(parametrizeURL(PATH.CREATE_PROPOSAL, { appointmentId }));
        },
        [navigate],
    );

    const addInvoiceHandler = useCallback(
        (appointmentId: string) => {
            navigate(parametrizeURL(PATH.CREATE_INVOICE, { appointmentId }));
        },
        [navigate],
    );

    const viewHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.APPOINTMENT_DETAILS, { id }));
        },
        [navigate],
    );

    const applyFiltersHandler = useCallback(
        (values: TableFiltersState) => {
            loadList({ page: 0, ...values });
        },
        [loadList],
    );

    const extraComponents = useMemo(
        () => ({
            Actions: ({ rowData }: { rowData: AppointmentResponseViewModel }) => {
                const deleteHandler = (appointmentId: string) => {
                    setSelectedItem({ id: appointmentId, name: rowData.title });
                    removeModal.openModal();
                };

                return (
                    <AppointmentsActionsMenu
                        id={rowData.id}
                        invoiceId={rowData.invoiceId}
                        onOpen={viewHandler}
                        onDelete={deleteHandler}
                        onEdit={editHandler}
                        onAdd={addProposalHandler}
                        onInvoiceAdd={addInvoiceHandler}
                    />
                );
            },
            Row: ({ rowData }: { rowData: AppointmentResponseViewModel }) => (
                <AppointmentListRow {...{ rowData, columns: tableColumns }} />
            ),
            Cell: ({ rowData, column }: { rowData: AppointmentResponseViewModel; column: ITableHeadCell }) => (
                <AppointmentListCell {...{ rowData, item: column }} />
            ),
        }),
        [removeModal, viewHandler, editHandler],
    );

    const filterParams = useMemo(() => getFilterParams(state.tableParams), [state.tableParams]);

    useDidMount(() => {
        loadList(state.tableParams);
    });

    useDeniedAccess(ONLY_CONTRACTOR);

    const { hasAccess } = useRolePermissions(ONLY_USER);

    if (state.list.length === 0 && !state.isLoading && !getHasFilterParams(filterParams)) {
        return (
            <Box height={`calc(100vh - ${mainHeaderHeight}px)`}>
                <NoListYet />
            </Box>
        );
    }

    return (
        <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            px={2.5}
            py={2}
        >
            <Headline title="Your Appointments" />
            <Box width="100%">
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    rowGap={2}
                    width="100%"
                    mb={2}
                    flexWrap="wrap"
                >
                    {hasAccess && (
                        <Button onClick={createNewAppointment} variant="outlined">
                            Create new Appointment
                        </Button>
                    )}
                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField onChange={searchChangeHandler} size="small" label="Search">
                            Search
                        </TextField>
                        <TableFilters
                            defaultValues={filterParams}
                            statuses={APPOINTMENT_STATUS_OPTIONS}
                            onApplyFilters={applyFiltersHandler}
                        />
                    </Box>
                </Box>
                {!!state.list.length && (
                    <AdaptiveTable
                        rows={state.list}
                        columns={tableColumns}
                        tableParams={state.tableParams}
                        totalCount={state.totalCount}
                        components={extraComponents}
                        onSort={sortHandler}
                        onSetPage={setPageHandler}
                        onSetRowsPerPage={setRowsPerPageHandler}
                    />
                )}
                {state.list.length === 0 && getHasFilterParams(filterParams) && <NoResultsFound />}
            </Box>
            {removeModal.open && (
                <WarningPopUp
                    isOpen={removeModal.open}
                    warningHeader="Remove Appointment?"
                    warningBody={`Are you sure you want to remove ${selectedItem.name} Appointment?`}
                    confirmButtonText="Remove"
                    onClose={removeModal.closeModal}
                    onConfirm={confirmDeleteHandler}
                />
            )}
            {state.isLoading && <BackdropLoader />}
        </Box>
    );
};
